import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const fadeUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(16px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const floatOrb = keyframes`
	0%,
	100% {
		transform: translate(0, 0);
	}
	50% {
		transform: translate(10px, -8px);
	}
`;

const spin = keyframes`
	to {
		transform: rotate(360deg);
	}
`;

const pulseDot = keyframes`
	0%,
	80%,
	100% {
		opacity: 0.28;
		transform: scale(0.85);
	}
	40% {
		opacity: 1;
		transform: scale(1);
	}
`;

const successPop = keyframes`
	0% {
		opacity: 0;
		transform: scale(0.82);
	}
	100% {
		opacity: 1;
		transform: scale(1);
	}
`;

export const PageShell = styled.div`
	position: relative;
	min-height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px 18px;
	overflow: hidden;
	background: ${THEME.bodyBackground};
	font-family: ${THEME.fontApp};
	color: ${THEME.text};

	&::before,
	&::after {
		content: '';
		position: absolute;
		border-radius: 50%;
		filter: blur(90px);
		pointer-events: none;
		animation: ${floatOrb} 9s ease-in-out infinite;
	}

	&::before {
		width: 380px;
		height: 380px;
		top: -120px;
		left: -90px;
		background: rgba(176, 38, 255, 0.24);
	}

	&::after {
		width: 320px;
		height: 320px;
		right: -70px;
		bottom: -90px;
		background: rgba(255, 77, 242, 0.16);
		animation-delay: -3s;
	}
`;

export const StatusCard = styled.section`
	position: relative;
	z-index: 1;
	width: min(100%, 440px);
	padding: 28px 26px 24px;
	border-radius: 22px;
	border: 1px solid rgba(246, 142, 255, 0.18);
	background:
		linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, transparent 42%),
		rgba(18, 9, 26, 0.92);
	box-shadow: ${THEME.shadow};
	backdrop-filter: blur(16px);
	animation: ${fadeUp} 0.45s ease;

	${(props) =>
		props.$phase === 'success' &&
		css`
			border-color: rgba(84, 214, 161, 0.28);
			box-shadow:
				0 24px 70px rgba(0, 0, 0, 0.55),
				0 0 40px rgba(84, 214, 161, 0.08);
		`}

	${(props) =>
		props.$phase === 'failed' &&
		css`
			border-color: rgba(255, 122, 146, 0.28);
		`}
`;

export const BrandRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 22px;
`;

export const BrandMark = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 0.72rem;
	font-weight: 800;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: ${THEME.gold};

	&::before {
		content: '';
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: ${THEME.gradientPrimary};
		box-shadow: 0 0 12px rgba(255, 77, 242, 0.55);
	}
`;

export const StatusPill = styled.span`
	display: inline-flex;
	align-items: center;
	padding: 5px 10px;
	border-radius: 999px;
	font-size: 0.68rem;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	border: 1px solid transparent;

	${(props) => {
		if (props.$phase === 'verifying') {
			return css`
				color: #f6b8ff;
				background: rgba(176, 38, 255, 0.12);
				border-color: rgba(176, 38, 255, 0.28);
			`;
		}

		if (props.$phase === 'success') {
			return css`
				color: #9ef0c8;
				background: rgba(84, 214, 161, 0.1);
				border-color: rgba(84, 214, 161, 0.28);
			`;
		}

		return css`
			color: #ffb0be;
			background: rgba(255, 80, 110, 0.1);
			border-color: rgba(255, 122, 146, 0.28);
		`;
	}}
`;

export const VisualBlock = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 8px 4px 0;
`;

export const SpinnerRing = styled.div`
	position: relative;
	width: 72px;
	height: 72px;
	margin-bottom: 18px;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: 50%;
		border: 3px solid rgba(255, 255, 255, 0.06);
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: 50%;
		border: 3px solid transparent;
		border-top-color: ${THEME.pink};
		border-right-color: ${THEME.violet};
		animation: ${spin} 0.9s linear infinite;
	}
`;

export const IconBadge = styled.div`
	display: grid;
	place-items: center;
	width: 72px;
	height: 72px;
	margin-bottom: 18px;
	border-radius: 50%;
	animation: ${successPop} 0.35s ease;

	${(props) =>
		props.$phase === 'success'
			? css`
					color: ${THEME.green};
					background: rgba(84, 214, 161, 0.1);
					border: 1px solid rgba(84, 214, 161, 0.28);
					box-shadow: 0 0 28px rgba(84, 214, 161, 0.16);
				`
			: css`
					color: #ff8ea0;
					background: rgba(255, 80, 110, 0.08);
					border: 1px solid rgba(255, 122, 146, 0.24);
				`}

	svg {
		width: 34px;
		height: 34px;
		stroke: currentColor;
	}
`;

export const Title = styled.h1`
	margin: 0 0 10px;
	font-family: ${THEME.fontDisplay};
	font-size: clamp(1.2rem, 3.8vw, 1.45rem);
	line-height: 1.2;
	color: ${THEME.text};
`;

export const Message = styled.p`
	margin: 0;
	max-width: 34ch;
	font-size: 0.88rem;
	line-height: 1.55;
	color: rgba(216, 189, 228, 0.92);
`;

export const DotsRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-top: 18px;

	span {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: ${THEME.gradientPrimary};
		animation: ${pulseDot} 1.2s ease-in-out infinite;

		&:nth-child(2) {
			animation-delay: 0.15s;
		}

		&:nth-child(3) {
			animation-delay: 0.3s;
		}
	}
`;

export const RedirectNote = styled.p`
	margin: 18px 0 0;
	font-size: 0.76rem;
	color: ${THEME.soft};
`;

export const Actions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 22px;
`;

export const PrimaryButton = styled.button`
	width: 100%;
	padding: 14px 18px;
	border: 0;
	border-radius: 14px;
	font-family: ${THEME.fontApp};
	font-size: 0.92rem;
	font-weight: 800;
	color: #fff;
	background: ${THEME.gradientPrimary};
	box-shadow: 0 10px 28px rgba(176, 38, 255, 0.28);
	cursor: pointer;
	transition:
		transform 0.2s ease,
		opacity 0.2s ease;

	&:hover {
		transform: translateY(-1px);
		opacity: 0.95;
	}
`;

export const SecondaryLink = styled.a`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 12px 18px;
	border-radius: 14px;
	font-size: 0.84rem;
	font-weight: 600;
	color: ${THEME.muted};
	text-decoration: none;
	border: 1px solid rgba(255, 255, 255, 0.1);
	background: rgba(255, 255, 255, 0.03);
	transition:
		color 0.2s ease,
		border-color 0.2s ease;

	&:hover {
		color: ${THEME.text};
		border-color: rgba(255, 255, 255, 0.18);
	}
`;
